import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import AnimatedSection from "./AnimatedSection";
import {
  ShoppingCart, Factory, Warehouse, Tag, Rocket,
  ArrowRight, MessageCircle, Check,
} from "lucide-react";

const WHATSAPP_BASE = "https://wa.me/34672808030?text=";

const icons = [ShoppingCart, Factory, Warehouse, Tag, Rocket];

const UseCaseSimulator = () => {
  const { lang } = useLang();
  const t = translations[lang].simulator;
  const [selected, setSelected] = useState(null);

  const whatsappUrl = selected !== null
    ? `${WHATSAPP_BASE}${encodeURIComponent(t.profiles[selected].whatsapp)}`
    : WHATSAPP_BASE;

  return (
    <section data-testid="simulator-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <div className="text-center mb-12">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric mb-4 font-body">
              {t.overline}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-navy font-heading mb-4">
              {t.headline}
            </h2>
            <p className="text-lg text-slate-500 font-body">{t.subheadline}</p>
          </div>
        </AnimatedSection>

        {/* Profile selector */}
        <AnimatedSection delay={0.1}>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {t.profiles.map((profile, i) => {
              const Icon = icons[i];
              const isActive = selected === i;
              return (
                <button
                  key={i}
                  data-testid={`simulator-profile-${i}`}
                  onClick={() => setSelected(isActive ? null : i)}
                  className={`flex items-center gap-2.5 px-5 py-3 rounded-full text-sm font-medium transition-all duration-300 border font-body ${
                    isActive
                      ? "bg-electric text-white border-electric shadow-lg shadow-electric/20"
                      : "bg-white text-slate-600 border-slate-200 hover:border-electric/40 hover:text-navy"
                  }`}
                >
                  <Icon size={16} />
                  {profile.label}
                </button>
              );
            })}
          </div>
        </AnimatedSection>

        {/* Result */}
        <AnimatePresence mode="wait">
          {selected !== null && (
            <motion.div
              key={selected}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="max-w-3xl mx-auto"
            >
              <div
                data-testid="simulator-result"
                className="bg-navy rounded-3xl p-8 md:p-10 text-white relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-electric/10 rounded-full -mr-20 -mt-20 blur-3xl" />
                <div className="relative z-10">
                  <h3 className="text-xl md:text-2xl font-bold mb-2 font-heading">
                    {t.profiles[selected].title}
                  </h3>
                  <p className="text-slate-300 text-sm mb-6 font-body">
                    {t.profiles[selected].situation}
                  </p>

                  <p className="text-xs uppercase tracking-wider text-electric-light font-semibold mb-4 font-body">
                    {t.whatWeDo}
                  </p>
                  <div className="space-y-3 mb-8">
                    {t.profiles[selected].actions.map((action, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                        <span className="text-slate-200 text-sm font-body">{action}</span>
                      </div>
                    ))}
                  </div>

                  <p className="text-emerald-300 font-semibold text-sm mb-6 font-body">
                    {t.profiles[selected].result}
                  </p>

                  <a
                    data-testid="simulator-cta"
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-electric text-white font-semibold py-3.5 px-7 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/30 text-sm"
                  >
                    <MessageCircle size={16} />
                    {t.cta}
                    <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {selected === null && (
          <AnimatedSection delay={0.2}>
            <p className="text-center text-slate-400 text-sm font-body mt-4">{t.prompt}</p>
          </AnimatedSection>
        )}
      </div>
    </section>
  );
};

export default UseCaseSimulator;
